/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import axios from "axios";
import InputField from "components/fields/InputField";
import {
  Button,
  Radio,
  RadioGroup,
  Stack,
  FormLabel,
  Select,
} from "@chakra-ui/react";

const AccountLedgerAdjustmentTab: React.FC<{
  detail: any;
  setSwalProps: any;
  setIsProcessing: any;
  isProcessing: boolean;
  fetchAccountDetail: any;
}> = ({
  detail,
  setSwalProps,
  setIsProcessing,
  isProcessing,
  fetchAccountDetail,
}) => {
  const initialData: {
    amount: string;
    reference: string;
    asset: string;
  } = {
    amount: "",
    reference: "",
    asset: "",
  };

  const [accountDetailForEdit, setAccountDetailForEdit] = useState<any>(null);
  const [ledgerAdjustmentData, setLedgerAdjustmentData] =
    useState<any>(initialData);
  const [selectedType, setSelectedType] = useState<any>("deposit");

  useEffect(() => {
    setAccountDetailForEdit(detail);
  }, [detail]);

  // Check if there are any assets available
  const hasAssets =
    detail?.account_methods &&
    detail.account_methods.length > 0;

  // Helper function to get unique assets with network info from account_methods
  const getUniqueAssets = (accountMethods: any[]) => {
    // First, group methods by asset to count networks
    const assetNetworkCount = new Map();
    accountMethods?.forEach((method: any) => {
      if (method.network && method.network.length > 0) {
        const currentCount = assetNetworkCount.get(method.asset) || 0;
        assetNetworkCount.set(
          method.asset,
          currentCount + method.network.length
        );
      }
    });

    const assetList: any[] = [];

    accountMethods?.forEach((method: any) => {
      const hasMultipleNetworks = assetNetworkCount.get(method.asset) > 1;

      // If the method has networks
      if (method.network && method.network.length > 0) {
        method.network.forEach((net: any) => {
          assetList.push({
            asset: method.asset,
            network: net.network,
            displayName: hasMultipleNetworks
              ? `${method.asset}-${net.display_name}`
              : method.asset,
            value: hasMultipleNetworks
              ? `${method.asset}-${net.network}`
              : method.asset,
            crypto: method.crypto,
            decimal: method.decimal,
          });
        });
      } else {
        // No network, use asset name as is
        assetList.push({
          asset: method.asset,
          network: null,
          displayName: method.asset,
          value: method.asset,
          crypto: method.crypto,
          decimal: method.decimal,
        });
      }
    });

    // Remove duplicates based on value
    const uniqueMap = new Map();
    assetList.forEach((item) => {
      if (!uniqueMap.has(item.value)) {
        uniqueMap.set(item.value, item);
      }
    });

    return Array.from(uniqueMap.values());
  };

  // Helper function to get decimal places for selected asset
  const getSelectedAssetDecimal = () => {
    if (!ledgerAdjustmentData.asset || !detail?.account_methods) return 2;

    const selectedAsset = getUniqueAssets(detail.account_methods).find(
      (asset) => asset.value === ledgerAdjustmentData.asset
    );

    return selectedAsset?.decimal || 2;
  };

  // Add validation function for decimal places
  const validateDecimalPlaces = (
    value: string,
    maxDecimals: number
  ): boolean => {
    if (!value || !value.includes(".")) return true;

    const decimalPart = value.split(".")[1];
    return decimalPart ? decimalPart.length <= maxDecimals : true;
  };

  const handleInputChange = async (event: any) => {
    const { id, value } = event.target;

    // Reset amount and reference when asset changes
    if (id === "asset") {
      setLedgerAdjustmentData({
        ...ledgerAdjustmentData,
        [id]: value,
        amount: "",
        reference: "",
      });
      return;
    }

    if (id === "amount" && ledgerAdjustmentData.asset) {
      const maxDecimals = getSelectedAssetDecimal();

      // Check if the value has more decimal places than allowed
      if (!validateDecimalPlaces(value, maxDecimals)) {
        return; // Don't update state if validation fails
      }
    }

    setLedgerAdjustmentData({ ...ledgerAdjustmentData, [id]: value });
  };

  const handleRadioButtonChange = (event: any) => {
    setSelectedType(event.target.value);
  };

  const getSelectedAssetInfo = () => {
    if (!ledgerAdjustmentData.asset || !detail?.account_methods) {
      return { crypto: false, decimal: 2, asset: "", network: null };
    }

    const selectedAsset = getUniqueAssets(detail.account_methods).find(
      (asset) => asset.value === ledgerAdjustmentData.asset
    );

    return {
      crypto: selectedAsset?.crypto || false,
      decimal: selectedAsset?.decimal || 2,
      asset: selectedAsset?.asset || "",
      network: selectedAsset?.network || null,
    };
  };

  // Updated form validation
  const isFormValid =
    accountDetailForEdit &&
    accountDetailForEdit.id &&
    selectedType &&
    ledgerAdjustmentData?.amount &&
    ledgerAdjustmentData?.reference &&
    ledgerAdjustmentData?.asset &&
    (!ledgerAdjustmentData?.amount ||
      validateDecimalPlaces(
        ledgerAdjustmentData.amount,
        getSelectedAssetDecimal()
      ));

  const handleSubmit = async () => {
    if (isFormValid) {
      const assetInfo = getSelectedAssetInfo();
      const postData: any = {
        accountid: accountDetailForEdit.id,
        type: selectedType,
        amount: ledgerAdjustmentData.amount,
        reference: ledgerAdjustmentData.reference,
        asset: assetInfo.asset,
        username: localStorage.getItem("username"),
        userRole: localStorage.getItem("userRole"),
        crypto: assetInfo.crypto,
        amount_decimal: assetInfo.decimal,
      };

      // Only include network if asset is crypto and has a network
      if (assetInfo.crypto && assetInfo.network) {
        postData.network = assetInfo.network;
      }

      try {
        setIsProcessing(true);
        await axios
          .post(
            process.env.REACT_APP_API_URL +
              `/api/${localStorage
                .getItem("userRole")
                .toLowerCase()}/subaccount/action/account-ledger-adjustment`,
            postData
          )
          .then(function (response) {
            if (response && response.data?.status === "ok") {
              setSwalProps({
                show: true,
                icon: "success",
                title: "Success",
                html: `You have successfully created a ${selectedType} transaction!`,
                showConfirmButton: true,
                didClose: () => {
                  fetchAccountDetail(accountDetailForEdit.id, true);
                  setSwalProps({});
                  setIsProcessing(false);
                  setLedgerAdjustmentData(initialData);
                },
              });
            } else {
              setIsProcessing(false);
              setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: response.data.message,
                showConfirmButton: true,
                didClose: () => {
                  setSwalProps({});
                  setIsProcessing(false);
                },
              });
            }
          })
          .catch(function (error) {
            setIsProcessing(false);
            setSwalProps({
              show: true,
              icon: "error",
              title: "Oops!",
              html: error.response.data.message,
              showConfirmButton: true,
              didClose: () => {
                setSwalProps({});
                setIsProcessing(false);
              },
            });
          });
      } catch (error) {
        setIsProcessing(false);
        setSwalProps({
          show: true,
          icon: "error",
          title: "Oops!",
          html: "Something went wrong, please try again later.",
          showConfirmButton: true,
          didClose: () => {
            setSwalProps({});
            setIsProcessing(false);
          },
        });
      }
    }
  };

  const handleAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow navigation and editing keys
    if (
      [
        "Backspace",
        "Delete",
        "Tab",
        "Escape",
        "Enter",
        "Home",
        "End",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
      ].includes(e.key)
    ) {
      return;
    }

    // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    if (e.ctrlKey && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) {
      return;
    }

    // Allow only digits and decimal point
    if (!/[0-9.]/.test(e.key)) {
      e.preventDefault();
      return;
    }

    // Prevent multiple decimal points
    if (e.key === "." && ledgerAdjustmentData.amount.includes(".")) {
      e.preventDefault();
      return;
    }

    // Check decimal places limit when typing after decimal point
    if (
      ledgerAdjustmentData.asset &&
      ledgerAdjustmentData.amount.includes(".")
    ) {
      const maxDecimals = getSelectedAssetDecimal();
      const currentValue = ledgerAdjustmentData.amount;
      const cursorPosition = (e.target as HTMLInputElement).selectionStart || 0;
      const decimalIndex = currentValue.indexOf(".");

      // If typing after decimal point and would exceed max decimals
      if (
        cursorPosition > decimalIndex &&
        currentValue.length - decimalIndex - 1 >= maxDecimals
      ) {
        // Only allow if we're replacing/selecting text
        const selectionLength =
          ((e.target as HTMLInputElement).selectionEnd || 0) - cursorPosition;
        if (selectionLength === 0) {
          e.preventDefault();
        }
      }
    }
  };

  return (
    <>
      {accountDetailForEdit !== null ? (
        !hasAssets ? (
          <div className="p-7">
            <p className="py-8 text-center text-sm text-gray-500">
              Please setup payout methods
            </p>
          </div>
        ) : (
        <div className="px-4 py-8">
          <div className="grid max-w-5xl grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-navy-700 dark:text-white">
                Asset <span className="text-red-500">*</span>
              </label>
              <Select
                disabled={isProcessing || !detail}
                id="asset"
                value={ledgerAdjustmentData.asset}
                onChange={handleInputChange}
                className="custom-select h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:outline-none sm:text-sm"
              >
                <option value="">-- Please Select --</option>
                {getUniqueAssets(detail?.account_methods) &&
                  getUniqueAssets(detail?.account_methods).filter(
                    (item: any) => !item.crypto
                  ).length > 0 && (
                    <optgroup label="Fiat">
                      {getUniqueAssets(detail?.account_methods)
                        ?.filter((item: any) => !item.crypto)
                        .map((item: any) => (
                          <option
                            key={item.value}
                            value={item.value}
                            data-decimal={item.decimal}
                          >
                            {item.displayName}
                          </option>
                        ))}
                    </optgroup>
                  )}
                {getUniqueAssets(detail?.account_methods) &&
                  getUniqueAssets(detail?.account_methods).filter(
                    (item: any) => item.crypto
                  ).length > 0 && (
                    <optgroup label="Crypto">
                      {getUniqueAssets(detail?.account_methods)
                        ?.filter((item: any) => item.crypto)
                        .map((item: any) => (
                          <option
                            key={item.value}
                            value={item.value}
                            data-decimal={item.decimal}
                          >
                            {item.displayName}
                          </option>
                        ))}
                    </optgroup>
                  )}
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-navy-700 dark:text-white">
                Type *
              </label>
              <div className="mt-2 flex items-center gap-6">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="type"
                    value="deposit"
                    checked={selectedType === "deposit"}
                    onChange={handleRadioButtonChange}
                    disabled={isProcessing}
                    className="form-radio text-primary cursor-pointer"
                  />
                  <span className="ml-2 text-sm text-navy-700">Deposit</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="type"
                    value="withdrawal"
                    checked={selectedType === "withdrawal"}
                    onChange={handleRadioButtonChange}
                    disabled={isProcessing}
                    className="form-radio text-primary cursor-pointer"
                  />
                  <span className="ml-2 text-sm text-navy-700">Withdrawal</span>
                </label>
              </div>
            </div>
            <div>
              <InputField
                disabled={isProcessing}
                variant="auth"
                extra="w-full shadow-sm"
                label="Amount"
                required={true}
                placeholder=""
                id="amount"
                type="number"
                autoComplete="off"
                value={ledgerAdjustmentData.amount || ""}
                onKeyDown={handleAmountKeyDown}
                onChange={handleInputChange}
              />
              {ledgerAdjustmentData.asset &&
                ledgerAdjustmentData.amount &&
                !validateDecimalPlaces(
                  ledgerAdjustmentData.amount,
                  getSelectedAssetDecimal()
                ) && (
                  <p className="mt-1 text-xs text-red-500">
                    Maximum {getSelectedAssetDecimal()} decimal places allowed
                    for {ledgerAdjustmentData.asset}
                  </p>
                )}
            </div>
            <div>
              <InputField
                disabled={isProcessing}
                variant="auth"
                extra="w-full shadow-sm"
                label="Transaction Reference"
                required={true}
                placeholder=""
                id="reference"
                type="text"
                autoComplete="off"
                value={ledgerAdjustmentData.reference}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <div className="my-3 flex flex-1 items-end justify-end pt-2">
            <Button
              onClick={handleSubmit}
              isDisabled={!isFormValid || isProcessing}
              isLoading={isProcessing}
              loadingText="Processing..."
              className="btn-primary relative overflow-hidden rounded-md px-6 py-2.5 text-sm font-medium text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="relative z-10 tracking-tight">Process</span>
              <span className="absolute inset-0 z-0 scale-0 rounded-md bg-white/20 opacity-0 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
            </Button>
          </div>
        </div>
        )
      ) : (
        <>
          <div
            className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
            style={{ animationDuration: "0.6s" }}
          ></div>
          <div
            className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
            style={{ animationDuration: "0.6s" }}
          ></div>
          <div
            className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
            style={{ animationDuration: "0.6s" }}
          ></div>
          <div
            className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
            style={{ animationDuration: "0.6s" }}
          ></div>
        </>
      )}
    </>
  );
};

export default AccountLedgerAdjustmentTab;
